/**
 * `POST /v1/conversations/{id}/resolve` (D08, §10.4) — encerrar e reabrir.
 *
 * ── A resposta diz o que a ENGINE aplicou, e o que ainda não aconteceu ────
 *
 * O fluxo da §10.4 tem três degraus: o usuário resolve no CliqCRM, o Chatwoot
 * registra e emite evento, e a API Core reage ao evento. Este comando cobre o
 * primeiro e confirma o segundo — os efeitos do terceiro (encerrar o relógio
 * operacional, registrar a atividade, criar a tarefa de próxima ação) são
 * ASSÍNCRONOS e ainda não aconteceram quando a resposta chega.
 *
 * Por isso a resposta carrega `efeitos_pendentes` em vez de fingir que está
 * tudo pronto. A alternativa seria devolver um 200 mudo e deixar a tela
 * adivinhar por que a linha do tempo ainda não mostra o encerramento — e a
 * §14.3 é explícita em não simular sucesso.
 *
 * ── Por que a situação vem da engine ──────────────────────────────────────
 *
 * `situacao` é o que o Chatwoot confirmou, não o que foi pedido. Os dois
 * divergem quando a conversa mudou de estado entre a leitura e o comando, e
 * ecoar o pedido esconderia a divergência — que é a Armadilha da task um passo
 * antes de virar métrica errada.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

export const EncerrarConversaSchema = z
  .object({
    /**
     * `resolvida` encerra; `aberta` reabre.
     *
     * Reabertura existe pelo mesmo motivo do encerramento: a conversa que
     * voltou precisa de um caminho que passe pela engine, e não de alguém
     * editando a projeção — que ficaria divergente do Chatwoot no primeiro
     * evento seguinte.
     */
    situacao: z.enum(['resolvida', 'aberta']),
    /**
     * Por que está sendo encerrada. Opcional e livre.
     *
     * NÃO é motivo de perda nem classificação: isso exigiria um vocabulário
     * que a revenda define, e inventá-lo aqui produziria relatório que ninguém
     * consegue auditar — a mesma razão que deixou taxa de conversão fora do
     * dashboard (C06/D07). Aqui é nota humana, e vira atividade na timeline.
     */
    motivo: z.string().min(1).max(500).optional(),
  })
  .strict();

export type EncerrarConversa = z.infer<typeof EncerrarConversaSchema>;

export const ConversaEncerradaSchema = z.object({
  /** O canônico da conversa, ecoado — o mesmo do caminho (§3.2). */
  id: IdSchema,
  /** O que a ENGINE confirmou. Ver o cabeçalho. */
  situacao: z.string(),
  /**
   * O que ainda vai acontecer, por evento.
   *
   * Existe para a tela poder dizer "encerrando…" em vez de afirmar que já
   * terminou. Lista vazia significaria que nada mais vem — e nunca é o caso
   * hoje, porque o carimbo do encerramento sempre chega pela projeção.
   */
  efeitos_pendentes: z.array(
    z.enum([
      /** O carimbo datado em `read.conversations.resolvida_em`. */
      'registro_do_encerramento',
      /** A linha na timeline do cliente. */
      'atividade',
      /** Só quando a revenda configurou a regra de próxima ação. */
      'tarefa_de_proxima_acao',
      /** Só com a flag de IA ligada (F04). */
      'resumo_de_ia',
    ]),
  ),
});

export type ConversaEncerrada = z.infer<typeof ConversaEncerradaSchema>;
